import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ConfigFiscal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [cfg, setCfg] = useState(null);
  const [aceita, setAceita] = useState("ambos");
  const [exigeNF, setExigeNF] = useState(false);
  const [diaInicio, setDiaInicio] = useState(1);
  const [diaLimite, setDiaLimite] = useState(5);
  const [dados, setDados] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [capacidade, setCapacidade] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
  }, [user]);

  useEffect(() => {
    if (!empresaId) return;
    api.get(`/empresas/${empresaId}/config-fiscal`).then(r => {
      const c = r.data;
      setCfg(c);
      setAceita(c.aceita_montador || "ambos");
      setExigeNF(!!c.exige_nota_fiscal);
      setDiaInicio(c.dia_inicio_emissao || 1);
      setDiaLimite(c.dia_limite_emissao || 5);
      setDados(c.dados_nota_fiscal || {});
    });
    api.get(`/empresas/${empresaId}/capacidade-elegivel`).then(r => setCapacidade(r.data));
  }, [empresaId, salvando]);

  const updateDados = (patch) => setDados(prev => ({ ...prev, ...patch }));

  const salvar = async () => {
    if (!empresaId) { toast.error("Selecione empresa"); return; }
    if (exigeNF && (!dados.razao_social || !dados.cnpj_tomador)) {
      toast.error("Para exigir NF, informe Razão Social e CNPJ do Tomador"); return;
    }
    setSalvando(true);
    try {
      await api.post(`/empresas/${empresaId}/config-fiscal`, {
        aceita_montador: aceita,
        exige_nota_fiscal: exigeNF,
        dados_nota_fiscal: dados,
        dia_inicio_emissao: diaInicio,
        dia_limite_emissao: diaLimite,
      });
      toast.success("Configuração fiscal salva");
      if (user?.role === "empresa") {
        try {
          const s = (await api.get("/empresa/condicoes-status")).data;
          if (!s.calendario_ok) {
            toast.info("Próximo passo: Calendário de fechamento e pagamento");
            navigate("/admin/calendario-fechamento");
          } else if (!s.tabela_ok) {
            toast.info("Próximo passo: Tabela de Valores");
            navigate("/admin/valores-servicos");
          } else {
            toast.info("Configuração completa — publique as condições para liberar o aceite da rede");
            navigate("/admin/condicoes");
          }
        } catch { /* segue na página */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-8">
        <div className="label-cap">Configuração · Empresa</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Configuração Fiscal</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Defina quem pode executar atendimentos para sua empresa e se há exigência de Nota Fiscal.
        </p>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Label className="label-cap">Empresa</Label>
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="mt-2 max-w-md" data-testid="cf-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {empresaId && (
        <div className="space-y-6">
          {/* CARTAO 1: Aceite de montadores */}
          <div className="border border-border bg-white p-6">
            <div className="label-cap text-[#142766] mb-3">Aceite de Montadores</div>
            <p className="text-xs text-muted-foreground mb-4">
              Esta escolha impacta diretamente a capacidade disponível na sua região.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { v: "ambos", t: "Qualquer Montador", d: "Aceita com CNPJ ativo OU sem CNPJ. Maior capacidade disponível." },
                { v: "apenas_cnpj_ativo", t: "Apenas com CNPJ Ativo", d: "Restritivo. Necessário se você exige Nota Fiscal." },
                { v: "apenas_sem_cnpj", t: "Apenas sem CNPJ", d: "Caso opte por contratar apenas pessoa física." },
              ].map(opt => (
                <label key={opt.v}
                  className={`border-2 p-4 cursor-pointer transition ${aceita === opt.v ? "border-[#142766] bg-[#142766]/5" : "border-border hover:border-foreground/30"}`}
                  data-testid={`cf-aceita-${opt.v}`}>
                  <input type="radio" name="aceita" value={opt.v} checked={aceita === opt.v}
                    onChange={(e) => setAceita(e.target.value)} className="sr-only" />
                  <div className="font-bold text-sm">{opt.t}</div>
                  <div className="text-xs text-muted-foreground mt-1">{opt.d}</div>
                </label>
              ))}
            </div>
          </div>

          {/* CARTAO 2: Exigência de NF */}
          <div className="border border-border bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-cap text-[#142766]">Nota Fiscal</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Quando ativo, montadores com CNPJ deverão emitir NF em até 5 dias após cada fechamento do calendário.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={exigeNF} onChange={(e) => setExigeNF(e.target.checked)}
                  data-testid="cf-exige-nf" />
                <span className="font-bold text-sm">Exigir NF</span>
              </label>
            </div>

            {exigeNF && (
              <>
                <div className="grid md:grid-cols-2 gap-3 mb-5">
                  <div>
                    <Label className="label-cap">Dia início emissão</Label>
                    <Input type="number" min="1" max="31" value={diaInicio}
                      onChange={(e) => setDiaInicio(parseInt(e.target.value) || 1)}
                      className="mt-1" data-testid="cf-dia-inicio" />
                  </div>
                  <div>
                    <Label className="label-cap">Dia limite emissão (padrão)</Label>
                    <Input type="number" min="1" max="31" value={diaLimite}
                      onChange={(e) => setDiaLimite(parseInt(e.target.value) || 5)}
                      className="mt-1" data-testid="cf-dia-limite" />
                    <div className="text-[11px] text-muted-foreground mt-1">
                      As pendências acompanham o Calendário de Fechamento: NF até 5 dias após cada fechamento
                      (ex.: 1–15 → dia 20 do mesmo mês · 16–31 → dia 05 do mês seguinte).
                      Após o prazo, o valor acumula para o próximo mês.
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="label-cap mb-3">Dados do Tomador (sua empresa)</div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Razão Social *</Label>
                      <Input value={dados.razao_social || ""} onChange={(e) => updateDados({ razao_social: e.target.value })}
                        data-testid="cf-razao" />
                    </div>
                    <div>
                      <Label className="text-xs">CNPJ do Tomador *</Label>
                      <Input value={dados.cnpj_tomador || ""} onChange={(e) => updateDados({ cnpj_tomador: e.target.value })}
                        data-testid="cf-cnpj-tomador" />
                    </div>
                    <div>
                      <Label className="text-xs">Inscrição Estadual</Label>
                      <Input value={dados.ie || ""} onChange={(e) => updateDados({ ie: e.target.value })}
                        data-testid="cf-ie" />
                    </div>
                    <div>
                      <Label className="text-xs">Inscrição Municipal</Label>
                      <Input value={dados.im || ""} onChange={(e) => updateDados({ im: e.target.value })}
                        data-testid="cf-im" />
                    </div>
                    <div>
                      <Label className="text-xs">Código do Serviço (LC 116)</Label>
                      <Input value={dados.codigo_servico || ""} onChange={(e) => updateDados({ codigo_servico: e.target.value })}
                        placeholder="Ex: 14.06" data-testid="cf-cod-servico" />
                    </div>
                    <div>
                      <Label className="text-xs">Alíquota ISS (%)</Label>
                      <Input value={dados.aliquota_iss || ""} onChange={(e) => updateDados({ aliquota_iss: e.target.value })}
                        type="number" step="0.01" data-testid="cf-aliquota" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Endereço Completo do Tomador</Label>
                      <Input value={dados.endereco || ""} onChange={(e) => updateDados({ endereco: e.target.value })}
                        data-testid="cf-endereco-tomador" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Descrição padrão do serviço (será impressa nas NFs)</Label>
                      <Textarea rows={2} value={dados.descricao_padrao || ""}
                        onChange={(e) => updateDados({ descricao_padrao: e.target.value })}
                        placeholder="Ex: Prestação de serviços de montagem de móveis..." data-testid="cf-descricao" />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* CARTAO 3: Impacto na Capacidade */}
          {capacidade && (
            <div className="border border-border bg-white p-6" data-testid="cf-capacidade">
              <div className="label-cap text-[#142766] mb-1">Impacto na Capacidade Operacional</div>
              <p className="text-xs text-muted-foreground mb-5">
                Como o filtro acima impacta sua disponibilidade de montadores na rede.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Filtro ativo" v={
                  capacidade.filtro_aceita_montador === "apenas_cnpj_ativo" ? "Apenas CNPJ" :
                  capacidade.filtro_aceita_montador === "apenas_sem_cnpj" ? "Sem CNPJ" : "Qualquer"
                } small />
                <Stat label="Total na rede" v={capacidade.total_regiao} />
                <Stat label="Elegíveis p/ você" v={capacidade.montadores_elegiveis} color={
                  capacidade.montadores_elegiveis < capacidade.montadores_proporcionais ? "text-red-600" : "text-[#00A859]"
                } />
                <Stat label="Proporção de uso" v={`${Math.round((capacidade.proporcao_uso || 0) * 100)}%`} />
              </div>
              <div className="mt-4 border-l-4 border-[#FFCC00] bg-[#FFCC00]/10 p-3 text-xs">
                <strong>Recomendação do sistema:</strong> {capacidade.recomendacao}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button onClick={salvar} disabled={salvando}
              className="btn-sharp brand-bg text-white hover:bg-[#001A5E] px-8 h-12"
              data-testid="cf-salvar">
              {salvando ? "Salvando…" : "Salvar configuração fiscal"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v, color = "", small = false }) {
  return (
    <div>
      <div className="label-cap text-[10px]">{label}</div>
      <div className={`font-heading font-extrabold ${small ? "text-lg" : "text-3xl"} ${color}`}>{v}</div>
    </div>
  );
}
